<template>
    <v-card-text>
        <v-card>
            <v-card-title>
                <v-row no-gutters>
                    Experiment Design Configuration
                    <v-spacer />
                    <v-btn v-if="isEditing" variant="elevated" @click="cancel">
                        Cancel
                    </v-btn>
                    <v-btn v-if="isEditing" variant="elevated" color="primary" @click="save" class="ms-4">
                        Save Configuration
                    </v-btn>
                    <v-btn v-else-if="userCanEditExperimentDesignConfigurations" variant="elevated" color="primary" @click="isEditing = true" append-icon="$pencil">
                        Edit Configuration
                    </v-btn>
                    <v-tooltip width="25%">
                        <p class="text-left bg-warning ps-3" v-if="disableFinalizeExperimentButton && !hasSavedConditions">
                            No conditions configured
                        </p>
                        <br/>
                        <p class="text-left">
                            <i>An experiment design configuration is necessary for finalizing a draft experiment.</i><br><br>
                            <b>What does this do?</b><br>
                            - Creates all conditions, containers, and wells <br>
                            - Prevents any further edits to the experiment design configuration <br>
                            - Experiment will no longer be in a draft state <br>

                            <br/>
                        </p>

                        <template v-slot:activator="{ props }">
                            <div v-bind="props" class="d-inline-block">
                                <v-btn v-if="showFinalizeExperimentButton" :disabled="disableFinalizeExperimentButton" @click="showConfirmationDialog" color="error" class="ms-4">Finalize Draft Experiment</v-btn>
                            </div>
                        </template>
                    </v-tooltip>
                </v-row>
            </v-card-title>
            <v-card-text>
                <design-config-form
                    ref="experimentDesignConfigForm"
                    :model-value="experimentDesignConfig"
                    :experimental-unit-options="experimentalUnitOptions"
                    :num-of-conditions="numOfConditions"
                    :disabled="!isEditing"
                    @update:modelValue="onDesignConfigFormChanged"
                />
            </v-card-text>
        </v-card>
        <v-dialog v-model="showFinalizeDraftExperimentConfirmationDialog" width="50%">
            <v-card>
                <v-card-title>Finalize Draft Experiment</v-card-title>
                <v-card-text>
                    Are you sure you want to finalize draft experiment {{ experimentCode }}?
                </v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn variant="elevated" @click="closeConfirmationDialog">Cancel</v-btn>
                    <v-btn variant="elevated" color="primary" @click="finalizeDraftExperiment">Yes, Finalize</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card-text>
</template>
<script setup lang="ts">
import DesignConfigForm from "@/components/ExperimentDesignConfigs/DesignConfigForm.vue";
import {
    ExperimentalUnitOption,
    ExperimentDesignConfig,
    experimentDesignConfigsAPI, isExperimentGroupLevelDesignConfig, SavedExperimentDesignConfigWithOptions,
} from "@/services/experimentDesignConfigs/experimentDesignConfigsAPI";
import { ref, onMounted, computed } from "vue";
import { withLoadingScreen } from "@/lib/loading";
import { useToast } from "vue-toastification";
import { useAuth } from "@/lib/ts/auth";
import { experimentsAPI, ExperimentSetupStatus } from "@/services/experiments/experimentsAPI";
import _ from "lodash";
import { useRouter } from "vue-router";
import { AbstractSchemaConfig } from "@/services/schemas/manageSchemasAPI";
import { findConditionSchema } from "@/lib/thingUtils";

const router = useRouter();
const toast = useToast();
const auth = useAuth();

const { experimentCode, experimentSetupStatus, numOfConditions, schemaConfig } = defineProps<{
    experimentCode: string;
    experimentSetupStatus: ExperimentSetupStatus;
    numOfConditions: number | null;
    schemaConfig: AbstractSchemaConfig
}>();

const showFinalizeDraftExperimentConfirmationDialog = ref<boolean>(false);

const userCanEditExperimentDesignConfigurations = computed(() => {
    return auth.isAuthorizedForOneOf(
        "ExperimentDesignConfigs:Create",
        "ExperimentDesignConfigs:Update"
    ) && experimentSetupStatus == "Draft"
});

const showFinalizeExperimentButton = computed(() => {
    return experimentSetupStatus == "Draft";
})

const disableFinalizeExperimentButton = computed(() => {
    return isEditing.value === true || experimentDesignConfig.value === null || !hasSavedConditions.value;
})

onMounted(() => {
    fetchData();
});

const hasSavedConditions = computed(() => {
    return _.isNil(findConditionSchema(schemaConfig)) || (!_.isNil(numOfConditions) && numOfConditions > 0);
});

const isEditing = ref<boolean>(false);

const experimentDesignConfig = ref<ExperimentDesignConfig | null>(null);
const experimentalUnitOptions = ref<ExperimentalUnitOption[]>([]);
const experimentDesignConfigForm = ref();

function showConfirmationDialog() {
    showFinalizeDraftExperimentConfirmationDialog.value = true;
}

function closeConfirmationDialog() {
    showFinalizeDraftExperimentConfirmationDialog.value = false;
}

function onDesignConfigFormChanged(updatedExperimentDesignConfig: ExperimentDesignConfig) {
    experimentDesignConfig.value = updatedExperimentDesignConfig;
}

function cancel() {
    isEditing.value = false;
    fetchData();
}

async function save() {
    if (!userCanEditExperimentDesignConfigurations.value || _.isNil(experimentDesignConfig.value)) {
        return;
    }

    let designConfig = experimentDesignConfig.value;

    /*
     If we're using the inherited design config from the exp group, we need to remove the id so
     that we create a new experiment-level record on the backend instead of update the exp level
     one
     */
    if ("id" in designConfig && isExperimentGroupLevelDesignConfig(designConfig)) {
        delete designConfig.id;
    }

    if (designConfig && (await experimentDesignConfigForm.value.validate())) {
        await withLoadingScreen(async () => {
            if ("id" in designConfig) {
                const id = designConfig.id as string;
                await experimentDesignConfigsAPI.updateExperimentDesignConfigForExperiment(
                    experimentCode,
                    id,
                    designConfig
                );
            } else {
                await experimentDesignConfigsAPI.createExperimentDesignConfigForExperiment(
                    experimentCode,
                    designConfig
                );
            }
        });
        await fetchData();
        isEditing.value = false;
        toast.success("Experiment design config saved successfully");
    }
}

async function fetchData() {
    await withLoadingScreen(async () => {
        const experimentDesignConfigWithOptions: SavedExperimentDesignConfigWithOptions = await experimentDesignConfigsAPI.fetchForExperimentCode(experimentCode);
        experimentDesignConfig.value = experimentDesignConfigWithOptions.experimentDesignConfig;
        experimentalUnitOptions.value = experimentDesignConfigWithOptions.experimentalUnitOptionsFromSchema;
    });
}

async function finalizeDraftExperiment() {
    closeConfirmationDialog();
    await withLoadingScreen(async () => {
        await experimentsAPI.finalizeDraftExperiment(experimentCode);
        // Refresh the page
        router.go(0);
    });
}

</script>
